// shared/src/export/pax-deletions-store.ts
// D1 access for `pax_deletions` (migration 0124) — the PAX tombstone log. A hard DELETE on the
// source is invisible to a watermark-sliced incremental export (it only sees rows that still exist),
// so each semantic hard-delete appends a tombstone here in the SAME db.batch as the DELETE (D1's only
// all-or-nothing unit). The export slices these by deleted_at; the consumer replays them as deletes.
//
// Row/Zod/reader/writer live together and ship with the DDL in ONE commit (feedback_writer_ddl_lockstep).
// shared has no @cloudflare/workers-types, so we use narrow structural D1 interfaces (same convention as
// introspect.ts's PaxExportD1 / oracle-store.ts's OracleD1). tombstoneStmts is generic in the statement
// type S so the returned stmts keep the caller's concrete type (D1PreparedStatement / OracleD1Stmt) and
// drop straight into that db's `batch([...])` with no cast.

import { z } from 'zod';

// shared/ has no DOM/Workers lib in its tsconfig; the Workers runtime provides `crypto` globally
// (same declaration convention as drive/client.ts).
declare const crypto: { randomUUID(): string };

export interface PaxDeletionRow {
  readonly id:         string;   // surrogate PK (crypto.randomUUID) — export idempotency key
  readonly entity:     string;   // exported table name, e.g. 'oracle_feeds_config'
  readonly pk_json:    string;   // JSON object of the deleted row's PK, e.g. {"id":"..."}
  readonly deleted_at: string;   // ISO-Z (C018); the export watermark
}

export const PaxDeletionSchema = z.object({
  id:         z.string().min(1),
  entity:     z.string().min(1),
  pk_json:    z.string().min(1),
  deleted_at: z.string().min(1),
});

// ── Narrow WRITE surface: prepare → bind, generic in the bound-statement type S ──
export interface PaxDeletionsWriteD1<S> {
  prepare(sql: string): { bind(...values: unknown[]): S };
}

/** Build the tombstone INSERT statement(s) to batch ATOMICALLY alongside a hard DELETE. One stmt per
 *  deleted PK: `INSERT INTO pax_deletions (id, entity, pk_json, deleted_at) VALUES (?,?,?,?)` bound with
 *  a fresh crypto.randomUUID(), the entity (table name), JSON.stringify(pk), and the ISO-Z delete time.
 *  Caller MUST spread these into the SAME db.batch as the DELETE so the tombstone can never land without
 *  its delete (or vice-versa). */
export function tombstoneStmts<S>(
  db: PaxDeletionsWriteD1<S>,
  entity: string,
  pks: ReadonlyArray<Record<string, unknown>>,
  deletedAtIso: string,
): S[] {
  return pks.map((pk) =>
    db
      .prepare('INSERT INTO pax_deletions (id, entity, pk_json, deleted_at) VALUES (?, ?, ?, ?)')
      .bind(crypto.randomUUID(), entity, JSON.stringify(pk), deletedAtIso),
  );
}

// ── Narrow READ surface: prepare → bind → all (results optional, as real D1 declares it) ──
export interface PaxDeletionsReadD1 {
  prepare(sql: string): {
    bind(...values: unknown[]): { all<T = unknown>(): Promise<{ results?: T[]; success: boolean }> };
  };
}

/** Tombstones recorded strictly AFTER `sinceIso` (the export watermark), oldest first. This is the
 *  incremental slice the PAX export replays each run. */
export async function readPaxDeletionsSince(db: PaxDeletionsReadD1, sinceIso: string): Promise<PaxDeletionRow[]> {
  const res = await db
    .prepare('SELECT id, entity, pk_json, deleted_at FROM pax_deletions WHERE deleted_at > ? ORDER BY deleted_at ASC')
    .bind(sinceIso)
    .all<PaxDeletionRow>();
  return res.results ?? [];
}
